import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";

export default function TermsPage() {
  const lastUpdated = "07 July 2026";

  const termsSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Terms of Use — HANDICALC",
    description:
      "These terms explain how you may use the HANDICALC® website and Golf Shot Allowance Calculator.",
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(termsSchema) }}
      />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-primary-900">
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950 via-primary-900 to-primary-800"></div>
          <div
            className="absolute inset-0 bg-cover bg-center opacity-12"
            style={{
              backgroundImage:
                "url('https://readdy.ai/api/search-image?query=Abstract%20minimalist%20landscape%20with%20soft%20rolling%20green%20hills%20and%20scattered%20pine%20trees%20in%20early%20morning%20fog%2C%20muted%20sage%20and%20olive%20tones%2C%20painterly%20atmospheric%20style%2C%20calm%20serene%20editorial%20composition%20suitable%20as%20subtle%20dark%20background%20overlay&width=1600&height=600&seq=terms-hero-bg&orientation=landscape')",
            }}
          ></div>
          <div className="relative mx-auto max-w-content px-4 md:px-6 pt-20 md:pt-24 pb-12 md:pb-16 text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur border border-background-50/20 text-xs font-medium text-accent-300">
              <i className="ri-file-text-line"></i>
              Legal
            </span>
            <h1 className="mt-6 font-heading font-bold text-background-50 text-3xl md:text-5xl lg:text-6xl leading-tight max-w-3xl mx-auto">
              Terms of Use
            </h1>
            <p className="mt-4 text-primary-100 text-sm md:text-base">
              Last updated: {lastUpdated}
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-4 md:px-6">
            <div className="space-y-8 md:space-y-10">
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                These terms explain how you may use the HANDICALC® website and Golf Shot Allowance Calculator. By accessing or using the site, you agree to these terms.
              </p>

              {/* 1. Purpose */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  1. Purpose of HANDICALC®
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  HANDICALC® provides calculations designed to reflect World Handicap System™ methodology to help golfers and organisers determine Playing Handicaps and shot allowances. It is intended as a convenience tool and not as an official handicapping authority.
                </p>
              </div>

              {/* 2. Your responsibilities */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  2. Your responsibilities
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-2">
                  You agree to:
                </p>
                <ul className="space-y-2 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                  <li>Use the calculator reasonably and for lawful purposes</li>
                  <li>Verify outputs where accuracy is critical (e.g., official competitions)</li>
                  <li>Comply with the Rules of Handicapping and your club&apos;s Terms of Competition</li>
                </ul>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mt-4">
                  You acknowledge that Committee decisions and official WHS™ rules always override outputs from HANDICALC® where differences occur.
                </p>
              </div>

              {/* 3. Accuracy and availability */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  3. Accuracy and availability
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-4">
                  We aim for accuracy but cannot guarantee that all calculations, course data or information will always be correct, complete or up to date. Site availability may vary due to maintenance or technical issues.
                </p>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  You use HANDICALC® at your own risk. To the fullest extent permitted by law, we exclude liability for any loss, damage, decision or dispute arising from reliance on the calculator or its outputs.
                </p>
              </div>

              {/* 4. Fair and acceptable use */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  4. Fair and acceptable use
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-2">
                  You must not:
                </p>
                <ul className="space-y-2 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                  <li>Disrupt or interfere with the website or its infrastructure</li>
                  <li>Attempt to access data or systems without permission</li>
                  <li>Scrape or copy the service in ways that harm performance</li>
                  <li>Present HANDICALC® as an official WHS™ or handicapping authority</li>
                </ul>
              </div>

              {/* 5. Intellectual property */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  5. Intellectual property
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  The HANDICALC® name, branding, design, calculator logic and website content are protected by intellectual property rights. You may not reuse or republish these materials without permission, except where permitted by law.
                </p>
              </div>

              {/* 6. Third-party content */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  6. Third-party content and links
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  HANDICALC® may link to external sites such as golf clubs, unions or governing bodies. We do not endorse or control these sites and cannot guarantee the accuracy or availability of their content.
                </p>
              </div>

              {/* 7. Changes to the service */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  7. Changes to the service
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  We may update, modify or discontinue parts of the site — including formats, features or data sources — without prior notice.
                </p>
              </div>

              {/* 8. Changes to these terms */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  8. Changes to these terms
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  We may update these terms periodically. When changes occur, the &ldquo;Last updated&rdquo; date will be amended. Continued use of the site indicates acceptance of updated terms.
                </p>
              </div>

              {/* 9. Contact */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  9. Contact
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  If you have questions about these terms or how they apply to your use of HANDICALC®, please get in touch via the{" "}
                  <Link
                    to="/contact"
                    className="text-primary-700 hover:text-primary-800 font-medium underline underline-offset-2"
                  >
                    contact page
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-background-50 pb-16 md:pb-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="relative overflow-hidden rounded-3xl">
              <img
                src="https://readdy.ai/api/search-image?query=Tranquil%20golf%20course%20at%20dawn%20with%20soft%20mist%20over%20fairway%20and%20distant%20tree%20line%20silhouette%2C%20peaceful%20morning%20atmosphere%2C%20painterly%20editorial%20style%2C%20muted%20emerald%20green%20and%20warm%20earthy%20tones%2C%20contemplative%20landscape%20composition&width=1600&height=600&seq=terms-cta-bg&orientation=landscape"
                alt="Tranquil golf course at dawn"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-900/70 to-primary-800/55"></div>
              <div className="relative z-10 px-6 md:px-14 py-12 md:py-16 text-center max-w-2xl mx-auto">
                <h2 className="font-heading font-bold text-background-50 text-2xl md:text-3xl leading-tight">
                  Ready to get started?
                </h2>
                <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
                  <Link
                    to="/calculator"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-accent-500 hover:bg-accent-600 text-foreground-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Open the calculator
                  </Link>
                  <Link
                    to="/privacy"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-background-50/10 hover:bg-background-50/20 backdrop-blur border border-background-50/30 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Read privacy policy
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}